import { Box, Span } from '@chakra-ui/react';
import { Plus } from 'iconoir-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ContactPrimaryButton from '../ContactPrimaryButton';
import type { ContactShell } from '../contact-types';
import type { ContactView } from '../contact-views';
import ContactNavRow from './ContactNavRow';
import ContactSection from './ContactSection';
import { useRovingTabIndex } from './use-roving-tabindex';

interface ContactCollaborationsListProps {
  contact: ContactShell;
  view: ContactView;
  /** Drill the right panel into a collaboration view (and seed the collab id). */
  onSelectCollab: (view: ContactView, collabId: string) => void;
  /** "Propose a collaboration" affordance. Optional; omit to hide the CTA. */
  onPropose?: () => void;
  /**
   * Opens the wizard straight on UGC. Present only for a brand offered UGC,
   * which then reads a UGC row "to propose" among the collaborations.
   */
  onProposeUgc?: () => void;
  /** True while the recipient enrichment resolves after a propose click. */
  isProposing?: boolean;
}

/**
 * COLLABORATIONS rail section. One `ContactNavRow` per implemented collaboration
 * kind (Affiliation, Collabs and Partnership Ads); active rows show their status
 * sublabel, "to propose" rows read "À proposer". A header badge counts the
 * active collaborations. Below, a "Proposer une collaboration" primary dark CTA.
 *
 * The same section and generic propose CTA stay available for prospects and
 * existing partners. Rows navigate to tracking; only the CTA opens the shared
 * type picker. Inactive rows remain accessible for their collaboration history.
 * With no row to show, a quiet line says so rather than leaving the heading bare.
 *
 * Unimplemented kinds (Gifting) are filtered out. A brand offered UGC also
 * reads a UGC row that opens the propose wizard straight on UGC. Mentions and recent media are
 * not collaboration kinds: they live as tabs under the profile view, so they
 * have no rail entry here by design.
 *
 * Rows are a roving-tabindex single-select list; selecting drills the detail
 * panel into the matching view.
 */
const ContactCollaborationsList: React.FC<ContactCollaborationsListProps> = ({
  contact,
  view,
  onSelectCollab,
  onPropose,
  onProposeUgc,
  isProposing,
}) => {
  const { t } = useTranslation();
  const visibleCollaborations = useMemo(
    () => contact.collaborations.filter((c) => c.implemented),
    [contact.collaborations]
  );
  const activeCount = visibleCollaborations.filter((c) => c.active).length;

  const rovingItems = useMemo(
    () => visibleCollaborations.map(() => ({ disabled: false })),
    [visibleCollaborations]
  );
  const { getRowProps } = useRovingTabIndex(rovingItems);

  return (
    <ContactSection
      label={t('contactDrawer.rail.collaborations')}
      accessory={
        activeCount > 0 ? (
          <Span
            fontFamily="heading"
            fontSize="10px"
            fontWeight="semibold"
            lineHeight="1.4"
            color="green.700"
            bg="green.100"
            rounded="6px"
            px="8px"
            py="3px"
          >
            {t('contactDrawer.rail.activeCount', { count: activeCount })}
          </Span>
        ) : undefined
      }
    >
      {visibleCollaborations.length === 0 && !onProposeUgc ? (
        <Box fontFamily="body" fontSize="13px" color="primary.500" py="1">
          {t('contactDrawer.rail.collaborationsEmpty')}
        </Box>
      ) : (
        <Box role="list" display="flex" flexDirection="column" gap="2">
          {visibleCollaborations.map((collab, index) => {
            const { ref, tabIndex, onKeyDown } = getRowProps(index);
            return (
              <ContactNavRow
                key={collab.id}
                ref={ref}
                tabIndex={tabIndex}
                onKeyDown={onKeyDown}
                icon={{ kind: 'glyph', name: collab.icon }}
                title={collab.title}
                sublabel={collab.sublabel}
                selected={view === collab.view}
                onSelect={() => onSelectCollab(collab.view, collab.id)}
              />
            );
          })}
          {onProposeUgc && (
            // No tracking view of its own yet: the row is the way into the
            // wizard on UGC, outside the roving list of views.
            <ContactNavRow
              icon={{ kind: 'glyph', name: 'Camera' }}
              title={t('contactDrawer.rail.collabRows.ugc.title')}
              sublabel={t('contactDrawer.rail.collabRows.ugc.propose')}
              selected={false}
              onSelect={onProposeUgc}
            />
          )}
        </Box>
      )}

      {onPropose && (
        <ContactPrimaryButton
          onClick={onPropose}
          loading={isProposing}
          width="full"
          h="38px"
          mt="2.5"
        >
          <Plus width={15} height={15} />
          <Span>{t('contactDrawer.rail.proposeCollab')}</Span>
        </ContactPrimaryButton>
      )}
    </ContactSection>
  );
};

export default ContactCollaborationsList;
